import { useState } from "react";
import { useProfileStore } from "@/store/profile";
import patientRequest from "@/api/patient-request";
import { toast } from "sonner";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Label } from "@/components/ui/Label";
import { Button } from "@/components/ui/Button";

const MONTHS = [
  { text: "Jan", value: 1 },
  { text: "Feb", value: 2 },
  { text: "Mar", value: 3 },
  { text: "Apr", value: 4 },
  { text: "May", value: 5 },
  { text: "Jun", value: 6 },
  { text: "Jul", value: 7 },
  { text: "Aug", value: 8 },
  { text: "Sep", value: 9 },
  { text: "Oct", value: 10 },
  { text: "Nov", value: 11 },
  { text: "Dec", value: 12 },
];

// Labels stack above their fields on phones; from lg they sit in a right-aligned column.
const ROW_CLASS = "grid lg:grid-cols-[10rem_minmax(0,24rem)] lg:items-center lg:gap-x-4";
const LABEL_CLASS = "lg:mb-0 lg:text-right";

function getCardType(number: string): string {
  if (/^4/.test(number)) return "visa";
  if (/^(34|37)/.test(number)) return "amex";
  if (/^5[1-5]/.test(number)) return "mastercard";
  if (/^6011/.test(number)) return "discover";
  if (/^9792/.test(number)) return "troy";
  return "visa";
}

function formatCardNumber(value: string, isAmex: boolean): string {
  const d = value.replace(/\D/g, "");
  if (isAmex) {
    if (d.length <= 4) return d;
    if (d.length <= 10) return d.slice(0, 4) + " " + d.slice(4, 10);
    return d.slice(0, 4) + " " + d.slice(4, 10) + " " + d.slice(10, 15);
  }
  const groups = d.slice(0, 16).match(/.{1,4}/g) ?? [];
  return groups.join(" ");
}

export function PaymentForm() {
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const [nameOnAccount, setNameOnAccount] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [cvv, setCvv] = useState("");

  const facilityId = currentPatient?.facilityID?.toString() ?? "";
  const patientId = currentPatient?.patientID?.toString() ?? "";
  const cardType = getCardType(accountNo.replace(/\s/g, ""));
  const isAmex = cardType === "amex";

  const saveInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    const cvvLen = isAmex ? 4 : 3;
    if (cvv.length !== cvvLen) {
      toast.error("Invalid CVV");
      return;
    }
    const dateString = `${expMonth}/1/${expYear}`;
    const expDate = new Date(dateString);
    expDate.setMonth(expDate.getMonth() + 1);
    expDate.setDate(expDate.getDate() - 1);
    const m = expDate.getMonth() + 1;
    const y = expDate.getFullYear() % 100;
    const expMMYY = `${String(m).padStart(2, "0")}${String(y).padStart(2, "0")}`;

    const data = {
      facilityId,
      patientId,
      nameOnCard: nameOnAccount,
      acctNum: accountNo.replace(/\s/g, ""),
      expDate: expMMYY,
      cardCode: cvv,
    };

    try {
      const response = await patientRequest.request.capturecardalias(data);
      const res = response.data as { id?: number; value?: boolean } | undefined;
      if (res?.value) {
        toast.success("Credit Card details added");
      } else {
        toast.error("Failed adding credit card details");
      }
    } catch {
      toast.error("Failed adding credit card details");
    }
  };

  return (
    <form className="space-y-4 lg:m-4" onSubmit={saveInfo}>
      <h3 className="text-lg font-semibold">Credit Card Information</h3>
      <div className={ROW_CLASS}>
        <Label htmlFor="nameoncard" className={LABEL_CLASS}>
          Name on card:
        </Label>
        <Input
          type="text"
          id="nameoncard"
          value={nameOnAccount}
          onChange={(e) => setNameOnAccount(e.target.value)}
          required
        />
      </div>
      <div className={ROW_CLASS}>
        <Label htmlFor="cardnumber" className={LABEL_CLASS}>
          Card Number
        </Label>
        <Input
          type="text"
          id="cardnumber"
          value={accountNo}
          onChange={(e) => setAccountNo(formatCardNumber(e.target.value.replace(/\s/g, ""), isAmex))}
          placeholder={isAmex ? "XXXX XXXXXX XXXXX" : "XXXX XXXX XXXX XXXX"}
          maxLength={isAmex ? 17 : 19}
          required
        />
      </div>
      <div className={ROW_CLASS}>
        <Label htmlFor="expmonth" className={LABEL_CLASS}>
          Expiration Date:
        </Label>
        <div className="grid grid-cols-2 gap-2">
          <Select
            id="expmonth"
            value={expMonth}
            onChange={(e) => setExpMonth(e.target.value)}
            required
          >
            <option value="">Month</option>
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.text}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Expiration year"
            value={expYear}
            onChange={(e) => setExpYear(e.target.value)}
            required
          >
            <option value="">Year</option>
            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <div className={ROW_CLASS}>
        <Label htmlFor="cvv" className={LABEL_CLASS}>
          CVV/CID
        </Label>
        <Input
          type="number"
          id="cvv"
          value={cvv}
          onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, isAmex ? 4 : 3))}
          required
        />
      </div>
      <div className={ROW_CLASS}>
        <div className="hidden lg:block" />
        <Button type="submit" className="w-full sm:w-40">
          SAVE
        </Button>
      </div>
    </form>
  );
}
